import { Logo } from "../components/Logo";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, Sparkles, LogOut, Plus, FileDown, FileText, Table2, ShieldCheck, ShoppingBag } from "lucide-react";
import { useAuth } from "../auth";
import { useLanguage } from "../i18n";
import { ProfilePanel } from "../components/ProfilePanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountPage() {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
        setIsAuthenticated(true);
      } catch {
        setIsAuthenticated(false);
        navigate("/login");
      }
    };
    checkAuth();
  }, []);

  const loadRequests = async () => {
    try {
      const res = await axios.get(`${API}/my/requests`, { withCredentials: true });
      setRequests(res.data);
      return res.data;
    } catch {
      return [];
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    loadRequests();
    const interval = setInterval(async () => {
      const data = await loadRequests();
      if (data.every((r) => !["generating", "pending", "locked"].includes(r.ai_status))) {
        clearInterval(interval);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amberdark animate-spin" strokeWidth={1.5} />
      </div>
    );
  }

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div data-testid="account-page" className="min-h-screen bg-cream">
      <header className="border-b border-ink/10 bg-white/70 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="account-logo" className="flex items-center text-ink" aria-label="FArLight">
            <Logo className="h-9 w-auto" accent="#B8875A" />
          </Link>
          <div className="flex items-center gap-5">
            {user?.is_admin && (
              <Link to="/admin" data-testid="account-admin-link" className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-amberdark hover:text-ink transition-colors">
                <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                {t.account.adminArea}
              </Link>
            )}
            <span data-testid="account-credits" className="hidden sm:block text-xs uppercase tracking-[0.15em] text-inksoft">{t.account.credits}: <strong className="text-ink">{user?.credits ?? 0}</strong></span>
            <span data-testid="account-user-name" className="text-sm text-inksoft hidden sm:block">{user?.name}</span>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-inksoft hover:text-ink transition-colors"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
              {t.auth.logout}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
        <div className="flex items-center justify-between gap-6 flex-wrap">
          <h1 data-testid="account-title" className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-ink">
            {t.account.title}
          </h1>
          <Link
            to="/richiedi-concept?plan=base"
            data-testid="account-new-request"
            className="inline-flex items-center gap-2 bg-ink text-cream hover:bg-espresso transition-colors rounded-sm px-6 h-11 text-xs font-semibold uppercase tracking-[0.15em]"
          >
            <Plus className="w-4 h-4" strokeWidth={2} />
            {t.account.newRequest}
          </Link>
        </div>

        <ProfilePanel key={user?.user_id} />

        {requests.length === 0 ? (
          <p data-testid="account-empty" className="mt-12 text-inksoft">{t.account.empty}</p>
        ) : (
          <div className="mt-12 space-y-8">
            {requests.map((r) => (
              <div key={r.id} data-testid={`request-card-${r.id}`} className="bg-white border border-ink/10 rounded-sm p-8 shadow-[0_8px_30px_rgba(42,39,38,0.04)]">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h3 className="font-display text-2xl font-medium text-ink capitalize">{r.space_type} — {r.surface} mq</h3>
                    <p className="mt-1 text-xs uppercase tracking-[0.15em] text-inksoft">
                      {t.account.submittedOn} {new Date(r.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.plan === "base" && (
                      <span data-testid={`work-status-${r.id}`} className={`text-[10px] uppercase tracking-[0.2em] font-semibold rounded-sm px-3 py-1.5 ${r.work_status === "delivered" ? "bg-sage/20 text-sagedark" : r.work_status === "in_progress" ? "bg-[#C5A059]/15 text-[#8A6A2B]" : "bg-sand text-inksoft"}`}>
                        {t.account.workStatus?.[r.work_status || "received"] || r.work_status}
                      </span>
                    )}
                    <span className="text-[10px] uppercase tracking-[0.2em] font-semibold bg-sand text-inksoft rounded-sm px-3 py-1.5">
                      {r.plan ? t.pricing.plans[r.plan]?.name || r.plan : r.budget_range}
                    </span>
                  </div>
                </div>

                <div className="mt-6 border-t border-ink/10 pt-6">
                  <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-amberdark mb-4">
                    <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                    {t.account.aiPreview}
                  </p>
                  {r.ai_status === "ready" && r.human_review && (
                    <p data-testid={`human-review-${r.id}`} className="mb-4 text-xs text-sagedark font-semibold">{t.account.humanReview}</p>
                  )}
                  {r.plan === "base" && r.work_status === "delivered" ? (
                    <p data-testid={`delivered-${r.id}`} className="text-sm text-ink bg-sage/10 border border-sage/50 rounded-sm p-4">{t.account.deliveredNote}</p>
                  ) : r.plan === "base" && ["ready", "generating", "pending"].includes(r.ai_status) && (
                    <p data-testid={`in-progress-${r.id}`} className="text-sm text-ink bg-[#F7F5F0] border border-[#C5A059]/50 rounded-sm p-4">{t.twin.received} — {t.twin.receivedDesc}</p>
                  )}
                  {r.ai_status === "ready" && r.plan === "base" ? null : r.ai_status === "ready" ? (
                    <>
                      <div className="grid md:grid-cols-2 gap-6 mt-6">
                        <p data-testid={`ai-text-${r.id}`} className="text-sm leading-relaxed text-inksoft whitespace-pre-line">
                          {r.ai_preview_text}
                        </p>
                        {r.ai_image_path && (
                          <img
                            data-testid={`ai-image-${r.id}`}
                            src={`${API}/files/${r.ai_image_path}`}
                            alt={t.account.aiImageAlt}
                            className="rounded-sm border border-ink/10 w-full object-cover"
                            loading="lazy"
                          />
                        )}
                      </div>
                      {r.image_paths && Object.keys(r.image_paths).length > 1 && (
                        <div data-testid={`boards-${r.id}`} className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {Object.entries(r.image_paths).map(([k, pth]) => (
                            <a key={k} href={`${API}/files/${pth}`} target="_blank" rel="noopener noreferrer" data-testid={`board-${k}-${r.id}`} className="group block bg-sand/60 border border-ink/10 rounded-sm overflow-hidden">
                              <img src={`${API}/files/${pth}`} alt={t.account.boards[k] || k} loading="lazy" className="w-full aspect-square object-cover group-hover:opacity-90 transition-opacity" />
                              <p className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-inksoft">{t.account.boards[k] || k}</p>
                            </a>
                          ))}
                        </div>
                      )}
                      {r.products?.length > 0 && (
                        <div data-testid={`store-${r.id}`} className="mt-6 bg-white border border-amberdark/30 rounded-sm p-6">
                          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-amberdark mb-4">{t.account.store}</p>
                          <ul className="divide-y divide-ink/10">
                            {r.products.map((p, i) => (
                              <li key={i} data-testid={`product-${r.id}-${i}`} className="py-3 flex flex-wrap items-center justify-between gap-3 text-sm">
                                <div>
                                  <p className="text-ink font-semibold">{p.brand} {p.model} <span className="text-inksoft font-normal">× {p.qty}</span></p>
                                  <p className="text-xs text-inksoft">{p.zone} · {p.type}{p.code ? ` · cod. ${p.code}` : ""}{p.watt ? ` · ${p.watt} W` : ""}{p.cct ? ` · ${p.cct}` : ""}</p>
                                </div>
                                {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer sponsored" data-testid={`product-buy-${r.id}-${i}`} className="inline-flex items-center gap-2 bg-ink text-cream hover:bg-espresso rounded-sm px-4 h-9 text-[11px] font-semibold uppercase tracking-[0.15em]"><ShoppingBag className="w-3.5 h-3.5" strokeWidth={1.5} />{t.account.buy}</a>}
                              </li>
                            ))}
                          </ul>
                          <p className="mt-3 text-[11px] text-inksoft/70">{t.account.storeNote}</p>
                        </div>
                      )}
                      {r.ai_lamp_schedule && !r.products?.length && (
                        <div data-testid={`lamp-schedule-${r.id}`} className="mt-6 bg-sand/60 border border-ink/10 rounded-sm p-6">
                          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-sagedark mb-4">
                            {t.account.lampSchedule}
                          </p>
                          <p className="text-sm leading-relaxed text-inksoft whitespace-pre-line">{r.ai_lamp_schedule}</p>
                          <p className="mt-4 text-xs leading-relaxed text-inksoft/70">{t.account.lampScheduleNote}</p>
                        </div>
                      )}
                      {r.photometric && (
                        <div data-testid={`photometric-${r.id}`} className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {[
                            ["Em", `${r.photometric.em_lumen_method} lx`, r.photometric.compliance.em],
                            ["U0", r.photometric.u0, r.photometric.compliance.u0],
                            ["UGR", r.photometric.ugr, r.photometric.compliance.ugr],
                            ["LENI", `${r.photometric.leni_kwh_m2_year} kWh/mq·a`, true],
                          ].map(([k, v, ok]) => (
                            <div key={k} className="bg-sand/60 border border-ink/10 rounded-sm p-4">
                              <p className="text-[10px] uppercase tracking-[0.2em] text-inksoft">{k}</p>
                              <p className="mt-1 font-display text-2xl text-ink">{v}</p>
                              <p className={`text-[10px] uppercase tracking-[0.15em] font-semibold ${ok ? "text-sagedark" : "text-amberdark"}`}>{ok ? t.account.compliant : t.account.check}</p>
                            </div>
                          ))}
                          <p className="col-span-2 sm:col-span-4 text-xs text-inksoft">
                            {t.account.photometric} · {t.account.control}: {r.photometric.control.label} — {r.photometric.control.driver}
                          </p>
                        </div>
                      )}
                      <div className="mt-6 flex flex-wrap gap-3">
                        {r.pdf_path && r.plan !== "base" && !r.page_paths?.length && (
                          <a
                            data-testid={`download-pdf-${r.id}`}
                            href={`${API}/files/${r.pdf_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-ink text-cream hover:bg-espresso transition-colors rounded-sm px-5 h-10 text-xs font-semibold uppercase tracking-[0.15em]"
                          >
                            <FileDown className="w-4 h-4" strokeWidth={1.5} />
                            {t.account.downloadPdf}
                          </a>
                        )}
                        {r.bom_path && (
                          <a
                            data-testid={`download-bom-${r.id}`}
                            href={`${API}/files/${r.bom_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 border border-sagedark text-sagedark hover:bg-sagedark hover:text-cream transition-colors rounded-sm px-5 h-10 text-xs font-semibold uppercase tracking-[0.15em]"
                          >
                            <Table2 className="w-4 h-4" strokeWidth={1.5} />
                            {t.account.downloadBom}
                          </a>
                        )}
                        {r.editable_path && (
                          <a
                            data-testid={`download-editable-${r.id}`}
                            href={`${API}/files/${r.editable_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 border border-ink/20 text-ink hover:border-ink transition-colors rounded-sm px-5 h-10 text-xs font-semibold uppercase tracking-[0.15em]"
                          >
                            <FileText className="w-4 h-4" strokeWidth={1.5} />
                            {t.account.downloadEditable}
                          </a>
                        )}
                      </div>
                    </>
                  ) : r.ai_status === "manual" ? (
                    <p data-testid={`ai-quote-${r.id}`} className="text-sm text-amberdark">{t.account.quoteRequested}{r.estimate?.estimate_eur ? ` — ${t.quote.from} € ${Math.round(r.estimate.estimate_eur)}` : ""}</p>
                  ) : r.ai_status === "locked" ? (
                    <p data-testid={`ai-locked-${r.id}`} className="text-sm text-amberdark">{t.account.locked}</p>
                  ) : r.ai_status === "error" ? (
                    <p data-testid={`ai-error-${r.id}`} className="text-sm text-amberdark">{t.account.aiError}</p>
                  ) : (
                    <div data-testid={`ai-pending-${r.id}`} className="space-y-3">
                      <p className="flex items-center gap-3 text-sm text-inksoft">
                        <Loader2 className="w-4 h-4 animate-spin text-amberdark" strokeWidth={1.5} />
                        {t.account.aiGenerating}
                      </p>
                      <div className="flex gap-2" data-testid={`ai-stages-${r.id}`}>
                        {["concept", "immagini", "impaginazione"].map((s, k) => {
                          const idx = ["concept", "immagini", "impaginazione"].indexOf(r.ai_stage || "concept");
                          return <span key={s} className={`text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-sm border ${k <= idx ? "border-[#C5A059] text-[#C5A059]" : "border-ink/10 text-inksoft/50"}`}>{t.account.stages[s]}</span>;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <p data-testid="account-ip-notice" className="mt-16 text-[11px] leading-relaxed text-inksoft/60">{t.legal.ip}</p>
      </main>
    </div>
  );
}
